// @flow

import {shallowEqual} from 'app/utils/react-utils';
import type {contentType} from 'js-libs/sweetcommons/contentType';
import type {StoreShapeType} from 'app/reducers/types';
import type {PresenceMessageDataType} from 'app/presence/types';

import {PRESENCE_BEACON_WAIT_MULTIPLIER} from 'app/utils/constants';

import {accountSettingsSelector} from 'app/settings/api';
import {CurrentUserSelector} from 'app/current-user/api';

export function getGroupByDocument(
  ct: contentType,
  objectId: number | string
): string {
  return `${ct}.${objectId}`;
}

export function buildPresenceMessageContent(
  event: string,
  groups: Array<string>,
  state: StoreShapeType
) {
  return JSON.stringify({
    event: `presence.${event}`,
    data: {
      groups: groups
        .map((group) => ({
          group,
          // $FlowFixMe - https://github.com/facebook/flow/issues/7878
          groupData: state.presence.getIn([
            'subscriptions',
            group,
            'groupData',
          ]),
        }))
        .filter((group) => group.groupData != null),
    },
  });
}

export function getCurrentUserAndAccountIds(getState: () => StoreShapeType): {
  user_id: number,
  account_id: number,
} {
  const state = getState();
  return {
    user_id: CurrentUserSelector(state).data.id,
    account_id: accountSettingsSelector(state).data.account_id,
  };
}

export function setBeaconInterval(
  groupName: string,
  beaconInterval: number,
  getState: () => StoreShapeType,
  sendWebsocketMessage: (string) => void
): IntervalID {
  unsetBeaconInterval(groupName, getState);

  return setInterval(
    () =>
      sendWebsocketMessage(
        buildPresenceMessageContent('beacon', [groupName], getState())
      ),
    beaconInterval * 1000
  );
}

export function unsetBeaconInterval(
  groupName: string,
  getState: () => StoreShapeType
) {
  const intervalID = getState().presence.getIn([
    'subscriptions',
    groupName,
    'beaconIntervalID',
  ]);

  if (intervalID != null) {
    clearInterval(intervalID);
  }
}

export function setPresenceMemberTimeout(
  data: PresenceMessageDataType,
  getState: () => StoreShapeType,
  removePresenceMemberFunc: (data: PresenceMessageDataType) => void
): TimeoutID {
  unsetPresenceMemberTimeout(data, getState);
  return setTimeout(
    () => removePresenceMemberFunc(data),
    /* $FlowFixMe - at this point we know groupData exists because of the
           check on shouldUpdateMemberSubscription */
    data.groupData.beaconInterval * 1000 * PRESENCE_BEACON_WAIT_MULTIPLIER
  );
}

/* when user leaves a group, unset all group members timeouts */
export function unsetAllPresenceMembersTimeout(
  group: string,
  getState: () => StoreShapeType
) {
  const members = getState().presence.getIn(
    ['subscriptions', group, 'members'],
    {}
  );

  Object.keys(members).forEach((websocket_id) => {
    const {groupData} = members[websocket_id];
    unsetPresenceMemberTimeout(
      {websocket_id, group, groupData, stealth: false},
      getState
    );
  });
}

export function unsetPresenceMemberTimeout(
  data: PresenceMessageDataType,
  getState: () => StoreShapeType
) {
  const {group, websocket_id} = data;
  // $FlowFixMe - https://github.com/facebook/flow/issues/7878
  const timeoutID = getState().presence.getIn([
    'subscriptions',
    group,
    'members',
    websocket_id,
    'removalTimeoutID',
  ]);

  if (timeoutID != null) {
    clearTimeout(timeoutID);
  }
}

/* on beacon, attend or new-member message receive, check if the user should be
 * added/updated on/to redux store */
export function shouldUpdateMemberSubscription(
  event: string,
  messageData: PresenceMessageDataType,
  getState: () => StoreShapeType
) {
  const {group, websocket_id, groupData} = messageData;
  const {user_id, account_id} = getCurrentUserAndAccountIds(getState);

  const currentUserIsSubscribed =
    getState().presence.getIn(['subscriptions', group]) != null;

  // $FlowFixMe - https://github.com/facebook/flow/issues/7878
  const storedGroupData = getState().presence.getIn([
    'subscriptions',
    group,
    'members',
    websocket_id,
    'groupData',
  ]);

  return (
    // current user needs to be subscribed to channel
    currentUserIsSubscribed &&
    // groupData should exist
    groupData != null &&
    // message should have to be from a different user
    groupData.user_id !== user_id &&
    // and users are in the same account
    groupData.account_id === account_id &&
    // update with last beacon message data
    (event === 'beacon' ||
      event === 'join' ||
      /* use attend data only if its needed (if current user requested it)
            otherwise lets avoid update redux store and trigger re-render */
      (event === 'attend' && !shallowEqual(storedGroupData, groupData)))
  );
}
